// src/pages/admin/AdminSystemPage.tsx
import React from 'react';
import { useSystemHealth, useBackgroundJobs, useRetryJob, useClearFailedJobs, useClearCache } from '../hooks';
import { SystemStatus } from '../components';
import { useToast } from '@/core/hooks/useToast';

export const AdminSystemPage: React.FC = () => {
  const { data: health, isLoading: healthLoading } = useSystemHealth();
  const { data: jobs, isLoading: jobsLoading } = useBackgroundJobs();
  const { toast } = useToast();

  const retryJobMutation = useRetryJob();
  const clearFailedMutation = useClearFailedJobs();
  const clearCacheMutation = useClearCache();

  const handleRetryJob = (jobId: string) => {
    retryJobMutation.mutate(jobId, {
      onSuccess: () => toast({ title: 'Job queued for retry', variant: 'success' }),
    });
  };

  const handleClearCache = () => {
    if (confirm('Are you sure you want to clear the cache?')) {
      clearCacheMutation.mutate(undefined, {
        onSuccess: () => toast({ title: 'Cache cleared', variant: 'success' }),
      });
    }
  };

  const handleClearFailedJobs = () => {
    if (confirm('Are you sure you want to clear all failed jobs?')) {
      clearFailedMutation.mutate(undefined, {
        onSuccess: () => toast({ title: 'Failed jobs cleared', variant: 'success' }),
      });
    }
  };

  if (healthLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 animate-pulse rounded w-48" />
        <div className="h-64 bg-gray-200 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">System Status</h1>
          <p className="text-warm-gray">Monitor system health and background jobs</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleClearCache}
            disabled={clearCacheMutation.isPending}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-cream"
          >
            Clear Cache
          </button>
          <button
            onClick={handleClearFailedJobs}
            disabled={clearFailedMutation.isPending}
            className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
          >
            Clear Failed Jobs
          </button>
        </div>
      </div>

      {health && (
        <SystemStatus
          health={health}
          jobs={jobs?.items || []}
          onRetryJob={handleRetryJob}
          onClearCache={handleClearCache}
        />
      )}
    </div>
  );
};

export default AdminSystemPage;



